import React, { Component } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Row, Col, Card, Button, Input, message, Tabs } from "antd";
import { Animated } from "react-animated-css";
import {
    UserAddOutlined,
    ExportOutlined,
    ReloadOutlined,
    TeamOutlined,
} from "@ant-design/icons";
import { accountId } from '../../config';

import UserTable from "./components/UserTable/UserTable";
import UserModal from "./components/UserModal/UserModal";
import UserFilters from "./components/UserFilters/UserFilters";
import UserStats from "./components/UserStats/UserStats";
import BulkActions from "./components/BulkActions/BulkActions";
import AddUserModal from "./components/AddUserModal/AddUserModal";
import EditUserModal from "./components/EditUserModal/EditUserModal";

import "./UserManagement.scss";

// module
import { castList, castUpdatebyAdmin } from "../../modules/cast";
import { authRegisterByAdmin, authDeletebyAdmin } from "../../modules/auth";
import { byteliveUploadImage } from "../../modules/bytelive";

const { Search } = Input;

class UserManagement extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            refreshing: false,
            users: [],
            selectedUsers: [],
            totalUsers: 0,
            currentPage: 1,
            pageSize: 10,
            activeTab: "users",

            // Modal states
            userModalVisible: false,
            editingUser: null,
            addUserModalVisible: false,
            editUserModalVisible: false,

            // Filter states
            searchKeyword: "",
            statusFilter: "all",
            roleFilter: "all",
            dateRange: null,
            sortField: "createdAt",
            sortOrder: "descend",

            // Stats
            userStats: {
                totalUsers: 0,
                activeUsers: 0,
                bannedUsers: 0,
                newUsersToday: 0,
            },
        };
    }

    componentDidMount() {
        this.loadUsers();
        this.loadUserStats();
    }

    // Load users with filters
    loadUsers = (page = 1, size = 10) => {
        this.setState({ loading: true });

        const rawParams = {
            page,
            perPage: size,
            search: this.state.searchKeyword || undefined,
            sortOrder: this.state.sortOrder,
            showAll: true,
        };

        const params = Object.fromEntries(
            Object.entries(rawParams).filter(
                ([key, value]) => value !== undefined
            )
        );

        this.props
            .castList(params)
            .then((data) => {
                this.setState({
                    users: data.results,
                    totalUsers: data.total,
                    currentPage: page,
                    pageSize: size,
                    loading: false,
                });
            })
            .catch((error) => {
                console.error("Error loading users:", error);
                message.error("Failed to load users");
                this.setState({ loading: false });
            });
    };

    // Mock API call for loading users
    mockLoadUsers = (params) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                const mockUsers = this.generateMockUsers(params.size);
                resolve({
                    users: mockUsers,
                    total: 1247,
                    page: params.page,
                    size: params.size,
                });
            }, 500);
        });
    };

    // Generate mock users
    generateMockUsers = (count) => {
        const statuses = ["active", "inactive", "banned", "pending"];
        const roles = ["user", "streamer", "moderator", "admin"];
        const users = [];

        for (let i = 1; i <= count; i++) {
            users.push({
                id: i,
                username: `user${i}`,
                email: `user${i}@example.com`,
                displayName: `User ${i}`,
                avatar: null,
                status: statuses[Math.floor(Math.random() * statuses.length)],
                role: roles[Math.floor(Math.random() * roles.length)],
                createdAt: new Date(
                    Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000
                ).toISOString(),
                lastLogin: new Date(
                    Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000
                ).toISOString(),
                streamCount: Math.floor(Math.random() * 50),
                followerCount: Math.floor(Math.random() * 1000),
                verified: Math.random() > 0.7,
            });
        }

        return users;
    };

    // Load user statistics
    loadUserStats = () => {
        // TODO: Replace with actual API call
        setTimeout(() => {
            this.setState({
                userStats: {
                    totalUsers: 1247,
                    activeUsers: 892,
                    bannedUsers: 23,
                    newUsersToday: 15,
                },
            });
        }, 300);
    };

    // Handle search
    handleSearch = (value) => {
        this.setState({ searchKeyword: value }, () => {
            this.loadUsers(1, this.state.pageSize);
        });
    };

    // Handle filter change
    handleFilterChange = (filterType, value) => {
        this.setState({ [filterType]: value }, () => {
            this.loadUsers(1, this.state.pageSize);
        });
    };

    // Handle table change (pagination, sorting)
    handleTableChange = (pagination, filters, sorter) => {
        this.setState(
            {
                currentPage: pagination.current,
                pageSize: pagination.pageSize,
                sortField: sorter.field || "createdAt",
                sortOrder: sorter.order || "descend",
            },
            () => {
                this.loadUsers(pagination.current, pagination.pageSize);
            }
        );
    };

    // Handle user selection
    handleUserSelection = (selectedRowKeys, selectedRows) => {
        this.setState({ selectedUsers: selectedRows });
    };

    // Handle refresh
    handleRefresh = () => {
        this.setState({ refreshing: true });
        Promise.all([
            this.loadUsers(this.state.currentPage, this.state.pageSize),
            this.loadUserStats(),
        ]).finally(() => {
            this.setState({ refreshing: false });
        });
    };

    // Handle create user
    handleCreateUser = () => {
        this.setState({
            addUserModalVisible: true,
            editingUser: null,
        });
    };

    // Handle edit user
    handleEditUser = (user) => {
        this.setState({
            editUserModalVisible: true,
            editingUser: user,
        });
    };

    // Handle delete user
    handleDeleteUser = (user) => {
        this.props
            .authDeletebyAdmin(user.email)
            .then(() => {
                message.success(`User ${user.name} deleted successfully`);
                this.loadUsers(this.state.currentPage, this.state.pageSize);
            })
            .catch((err) => {
                message.error(`Failed to delete user ${user.name}`);
            });
    };

    // Handle user modal close
    handleUserModalClose = () => {
        this.setState({
            editUserModalVisible: false,
            addUserModalVisible: false,
            editingUser: null,
        });
    };

    // Handle user save
    handleUserSave = (userData) => {
        message.success(
            `User ${userData.username} ${this.state.editingUser ? "updated" : "created"
            } successfully`
        );
        this.handleUserModalClose();
        this.loadUsers(this.state.currentPage, this.state.pageSize);
        this.loadUserStats();
    };

    handleAddUserByAdmin = async (userData) => {
        try {
            await this.props.authRegisterByAdmin(userData);
            this.handleUserModalClose();
            this.loadUsers(this.state.currentPage, this.state.pageSize);
            this.loadUserStats();
        } catch (error) {
            throw error;
        }
    };

    handleUpdateUser = async (userId, updateData) => {
        try {
            await this.props.castUpdatebyAdmin(userId, updateData);
            this.handleUserModalClose();
            this.loadUsers(this.state.currentPage, this.state.pageSize);
            this.loadUserStats();
        } catch (error) {
            throw error;
        }
    };

    handleImageUpload = async (uploadData) => {
        try {
            const response = await this.props.byteliveUploadImage(uploadData);
            return response;
        } catch (error) {
            console.error("Upload error in UserManagement:", error);
            throw error;
        }
    };

    // Handle bulk actions
    handleBulkAction = (action) => {
        if (this.state.selectedUsers.length === 0) {
            message.warning("Please select users first");
            return;
        }

        message.success(
            `Bulk action "${action}" applied to ${this.state.selectedUsers.length} users`
        );
        this.setState({ selectedUsers: [] });
        this.loadUsers(this.state.currentPage, this.state.pageSize);
    };

    // Handle export
    handleExport = () => {
        message.success("Export functionality will be implemented");
    };

    // Handle tab change
    handleTabChange = (activeKey) => {
        this.setState({ activeTab: activeKey });
    };

    // Render Statistics Tab
    renderStatsTab = () => {
        const { userStats, loading, refreshing } = this.state;

        return (
            <div className="stats-tab-content">
                {/* Header Actions for Stats Tab */}
                <div className="tab-header-actions">
                    <Button
                        icon={<ExportOutlined />}
                        onClick={this.handleExport}
                    >
                        Export Stats
                    </Button>
                    <Button
                        icon={<ReloadOutlined spin={refreshing} />}
                        onClick={this.handleRefresh}
                        loading={refreshing}
                    >
                        Refresh
                    </Button>
                </div>

                {/* User Stats */}
                <UserStats stats={userStats} loading={loading} />

                {/* Additional stats content can be added here */}
                <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                    <Col span={24}>
                        <Card
                            title="User Activity Overview"
                            className="stats-card"
                        >
                            <p>
                                Additional statistics and charts can be placed
                                here.
                            </p>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    };

    // Render Users Management Tab
    renderUsersTab = () => {
        const {
            loading,
            refreshing,
            users,
            selectedUsers,
            totalUsers,
            currentPage,
            pageSize,
            searchKeyword,
            statusFilter,
            roleFilter,
            dateRange,
        } = this.state;
        const isHideAddUser = ['vivamax'].includes(accountId);

        return (
            <div className="users-tab-content">
                {/* Header Actions for Users Tab */}
                <div className="tab-header-actions">
                    {/* <Button
						icon={<ExportOutlined />}
						onClick={this.handleExport}
					>
						Export Users
					</Button> */}
                    <Button
                        icon={<ReloadOutlined spin={refreshing} />}
                        onClick={this.handleRefresh}
                        loading={refreshing}
                    >
                        Refresh
                    </Button>
                    {
                        !isHideAddUser &&
                        <Button
                            type="primary"
                            icon={<UserAddOutlined />}
                            onClick={this.handleCreateUser}
                        >
                            Add User
                        </Button>
                    }
                </div>

                {/* Filters and Search */}
                <Card className="filters-card">
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={12} md={12} lg={12}>
                            <Search
                                placeholder="Search username / email"
                                value={searchKeyword}
                                onChange={(e) =>
                                    this.setState({
                                        searchKeyword: e.target.value,
                                    })
                                }
                                onSearch={this.handleSearch}
                                allowClear
                            />
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]} align="middle">
                        {/* <Col xs={24} sm={24} md={24} lg={24}>
							<UserFilters
								statusFilter={statusFilter}
								roleFilter={roleFilter}
								dateRange={dateRange}
								onFilterChange={this.handleFilterChange}
							/>
						</Col> */}
                        <Col xs={24} sm={24} md={24} lg={24}>
                            <BulkActions
                                selectedUsers={selectedUsers}
                                onBulkAction={this.handleBulkAction}
                            />
                        </Col>
                    </Row>
                </Card>

                {/* User Table */}
                <Card className="table-card">
                    <UserTable
                        users={users}
                        loading={loading}
                        selectedUsers={selectedUsers}
                        totalUsers={totalUsers}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onTableChange={this.handleTableChange}
                        onUserSelection={this.handleUserSelection}
                        onEditUser={this.handleEditUser}
                        onDeleteUser={this.handleDeleteUser}
                    />
                </Card>
            </div>
        );
    };

    render() {
        const {
            activeTab,
            userModalVisible,
            editingUser,
            addUserModalVisible,
            editUserModalVisible,
        } = this.state;

        const tabItems = [
            // {
            // 	key: 'stats',
            // 	label: (
            // 		<span>
            // 			<BarChartOutlined />
            // 			Statistics
            // 		</span>
            // 	),
            // 	children: this.renderStatsTab()
            // },
            {
                key: "users",
                label: (
                    <span>
                        <TeamOutlined />
                        User Management
                    </span>
                ),
                children: this.renderUsersTab(),
            },
        ];

        return (
            <Animated animationIn="fadeIn">
                <div
                    className="user-management-wrapper"
                    style={{
                        paddingBottom: this.props.play ? "100px" : "20px",
                    }}
                >
                    {/* Header */}
                    <div className="page-header">
                        <div className="header-left">
                            <h2>User Management</h2>
                            <p>Manage platform users and their permissions</p>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="tabs-container">
                        <Tabs
                            activeKey={activeTab}
                            onChange={this.handleTabChange}
                            items={tabItems}
                            size="large"
                            className="user-management-tabs"
                        />
                    </div>

                    {/* User Modal */}
                    <EditUserModal
                        visible={editUserModalVisible}
                        onClose={this.handleUserModalClose}
                        onSubmit={this.handleUpdateUser}
                        userData={editingUser}
                        onImageUpload={this.handleImageUpload}
                    />

                    <AddUserModal
                        visible={addUserModalVisible}
                        onClose={this.handleUserModalClose}
                        onSubmit={this.handleAddUserByAdmin}
                    />
                </div>
            </Animated>
        );
    }
}

const mapStateToProps = (state) => ({
    router: state.router,
    userProfile: state.user.userProfile,
    selectedOrganization: state.user.selectedOrganization,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            castList,
            castUpdatebyAdmin,
            authRegisterByAdmin,
            authDeletebyAdmin,
            byteliveUploadImage,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(UserManagement);
